import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  IconButton,
  styled,
  DialogActions,
  Stack,
  Grid,
  Box,
} from "@mui/material";
import { Close, Attachment, PictureAsPdf } from "@mui/icons-material";
import { Spinner } from "../ui/Spinner";
import { uploadFileJp } from "src/redux/actions";
import { loadImage } from "src/functions/utils";
import ModalPersetujuanDownload from "./ModalPersetujuanDownload";
import ModalAlasanMundur from "./ModalAlasanMundur";

const CustomDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    backgroundColor: "rgb(255,255,255)",
    borderRadius: "1rem",
    border: "1px solid lightgrey",
  },
}));

const ModalConfirmOffering = ({
  open,
  onClose,
  dataDetail,
  loading,
  handleConfirmOffering,
}) => {
  useTheme();

  const [docs, setDocs] = useState([]);
  const [downloadDoc, setDownloadDoc] = useState({});
  const [openDownload, setOpenDownload] = useState(false);
  const [openResignReason, setOpenResignReason] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);

  // Sync docs dari dataDetail tiap modal dibuka
  useEffect(() => {
    if (open) {
      setDocs(dataDetail?.offering_doc || []);
    }
  }, [open, dataDetail]);

  const handleUploadCandidateDoc = async (e, index) => {
    setLoadingIndex(index);

    const res = await uploadFileJp(e.target.files[0]);
    if (typeof res === "string") {
      setLoadingIndex(null);
      // ganti sesuai cara kamu munculin error, misal setSnackbar
      alert(res);
      return;
    }

    const resFile = await loadImage(res.data.path);

    setDocs((prev) =>
      prev.map((doc, i) =>
        i === index
          ? {
              ...doc,
              candidate_upload: {
                path: res.data.path,
                new_path: resFile,
              },
            }
          : doc,
      ),
    );
    setLoadingIndex(null);
  };

  const handleOpenDownload = (doc) => {
    setDownloadDoc(doc);
    setOpenDownload(true);
  };

  const handleSubmitResignReason = (reason) => {
    handleConfirmOffering("TIDAK", docs, reason);
    setOpenResignReason(false);
  };

  const allDocsUploaded =
    docs.length > 0 && docs.every((doc) => doc?.candidate_upload?.path);

  return (
    <CustomDialog open={open} maxWidth="sm" fullWidth>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <b>Upload Document Offering</b>
        </DialogTitle>
        <IconButton sx={{ mr: "1rem" }} onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Stack spacing={2}>
          {docs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Belum ada dokumen offering.
            </Typography>
          )}

          {docs.map((doc, index) => (
            <Grid
              container
              spacing={1}
              key={index}
              alignItems="center"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                m: 0,
              }}
            >
              {/* Dokumen dari admin (read-only) */}
              <Grid item xs={7}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {doc.title || "Dokumen"}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleOpenDownload(doc)}
                >
                  Lihat / Download
                </Button>
              </Grid>

              {/* Upload balik dari kandidat */}
              <Grid item xs={5}>
                <input
                  accept="application/pdf"
                  style={{ display: "none" }}
                  id={`candidate-upload-${index}`}
                  type="file"
                  onChange={(e) => handleUploadCandidateDoc(e, index)}
                />

                {!doc.candidate_upload?.path ? (
                  <label htmlFor={`candidate-upload-${index}`}>
                    <Button
                      variant="contained"
                      component="span"
                      size="small"
                      startIcon={
                        loadingIndex === index ? <Spinner /> : <Attachment />
                      }
                      disabled={loadingIndex === index}
                      fullWidth
                    >
                      Upload
                    </Button>
                  </label>
                ) : (
                  <Stack spacing={0.5}>
                    <Box
                      component="a"
                      href={doc.candidate_upload.new_path}
                      download={doc.title ? `${doc.title}-kandidat.pdf` : true}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        textDecoration: "none",
                        border: "1px solid",
                        borderColor: "success.main",
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <PictureAsPdf color="success" fontSize="small" />
                      <Typography variant="caption" noWrap>
                        Terupload
                      </Typography>
                    </Box>
                    <label htmlFor={`candidate-upload-${index}`}>
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        Ganti file
                      </Typography>
                    </label>
                  </Stack>
                )}
              </Grid>
            </Grid>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setOpenResignReason(true)}
          disabled={loading}
        >
          Tidak, Saya mengundurkan diri
        </Button>
        <Button
          variant="contained"
          onClick={() => handleConfirmOffering("YA", docs)}
          disabled={loading || !allDocsUploaded}
        >
          Ya, lanjut proses sign contract
        </Button>
      </DialogActions>

      {openDownload && (
        <ModalPersetujuanDownload
          open={openDownload}
          onClose={() => setOpenDownload(false)}
          dataDetail={downloadDoc}
        />
      )}

      {openResignReason && (
        <ModalAlasanMundur
          open={openResignReason}
          onClose={() => setOpenResignReason(false)}
          onSubmit={handleSubmitResignReason}
          loading={loading}
        />
      )}
    </CustomDialog>
  );
};

export default ModalConfirmOffering;
